import { AvatarDropdown, AvatarName, Footer } from '@/components';
import type { MenuDataItem, Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import React from 'react';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import { getUserInfo } from './services/login';
import { DEFAULT_AVATAR, OSS_BASE } from './constants';
import { UserInfo } from './types/login';
import { MerchantSettings } from './types/merchant';
import { getMerchantConfig } from './services/merchant';
import { Alert, Space, theme } from 'antd';
import styles from './styles/layout.module.less';
import { UserStageType } from './types/member';
import SearchUserSelect from './components/SearchUserSelect';
import QuickOpenCardEntryButton from './pages/Member/QuickOpenCard/QuickOpenCardEntryButton';
import QuickOpenCardPortal from './pages/Member/QuickOpenCard/QuickOpenCardPortal';
import { LinkOutlined } from '@ant-design/icons';
import { getMenuBadge } from './constants/menu-badge';
import { ROUTE_KEY } from './constants/action-permission';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/login';
const DANCER_ID_CARD_VISIBLE_MERCHANT_IDS = [1, 2, 4];
const DANCER_ID_CARD_PATH = '/marketing-center/dancer-id-card';

const TutorialLink = () => {
  const { token } = theme.useToken();
  return (
    <Space size={6} align="center" style={{ height: 44 }}>
      <LinkOutlined style={{ color: token.colorPrimary }} />
      <Link to="/tutorial" target="_blank">
        系统教程
      </Link>
    </Space>
  );
};

const renderMenuBadge = (badge: string) => <span className={styles.menuBadge}>{badge}</span>;

const appendMenuBadge = (item: MenuDataItem, dom: React.ReactNode) => {
  const badge = getMenuBadge(item);
  if (!badge) {
    return dom;
  }
  return (
    <span className={styles.menuBadgeWrap}>
      {dom}
      {renderMenuBadge(badge)}
    </span>
  );
};

function filterDancerIdCardMenu(
  menuData: MenuDataItem[],
  showDancerIdCardMenu: boolean,
): MenuDataItem[] {
  if (showDancerIdCardMenu) {
    return menuData;
  }

  return menuData
    .filter(
      (item) =>
        item.key !== ROUTE_KEY.MarketingCenterDancerIdCard && item.path !== DANCER_ID_CARD_PATH,
    )
    .map((item) => ({
      ...item,
      children: item.children
        ? filterDancerIdCardMenu(item.children, showDancerIdCardMenu)
        : item.children,
    }));
}

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * getInitialState() 的返回值将成为全局初始状态
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: UserInfo;
  merchantConfig?: MerchantSettings; // 商户配置
  loading?: boolean;
}> {
  // 获取用户信息
  const fetchUserInfo = async () => {
    try {
      const data = await getUserInfo();
      return data;
    } catch (error) {
      history.push(loginPath);
    }
  };

  // 获取商户配置
  const fetchMerchantConfig = async () => {
    try {
      const data = await getMerchantConfig();
      return data;
    } catch (error) {
      console.error('获取商户配置失败', error);
    }
  };

  // 如果不是登录页面，执行
  const { location } = history;
  if (location.pathname !== loginPath) {
    const currentUser = await fetchUserInfo();
    const merchantConfig = await fetchMerchantConfig();
    return {
      currentUser,
      merchantConfig,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  return {
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  return {
    logo: `${OSS_BASE}/logo.png?v=1`,
    title: initialState?.merchantConfig?.merchant_name || '',
    breadcrumbRender: false,
    actionsRender: () => [
      IS_TEST_ENV ? <Alert key="alert" message="当前处于测试环境" type="warning" banner /> : null,
      <div key="quickOpenCard" className={styles.quickOpenCardEntry}>
        <QuickOpenCardEntryButton />
      </div>,
      <div key="SearchUserLeadsSelect" className={styles.searchUser}>
        <SearchUserSelect
          placeholder="请输入手机号搜索用户，发券、开卡"
          searchTypes={[UserStageType.USER, UserStageType.LEADS]}
          onChange={(user) => {
            if (user.type === UserStageType.LEADS) {
              window.open(`/clue-center/clue-done/detail?lead_id=${user.user_id}`);
            } else {
              window.open(`/member/detail/${user.user_id}`);
            }
          }}
        />
      </div>,
      <TutorialLink key="tutorial" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar || DEFAULT_AVATAR,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.username,
      fontColor: 'rgba(0,0,0,0.05)',
      fontSize: 12,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {},
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: [],
    menuHeaderRender: undefined,
    menuDataRender: (menuData) =>
      filterDancerIdCardMenu(
        menuData,
        DANCER_ID_CARD_VISIBLE_MERCHANT_IDS.includes(
          Number(initialState?.merchantConfig?.merchant_id),
        ),
      ),
    menuItemRender: (item, dom) => {
      const content = appendMenuBadge(item, dom);
      if (item.path) {
        return (
          <Link
            to={item.path}
            style={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}
          >
            {content}
          </Link>
        );
      }
      return content;
    },
    subMenuItemRender: (item, dom) => appendMenuBadge(item, dom),
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          <QuickOpenCardPortal />
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request = {
  ...errorConfig,
};

const originalError = console.error;
console.error = (...args) => {
  if (typeof args[0] === 'string' && args[0].includes('findDOMNode is deprecated')) {
    return;
  }
  originalError(...args);
};
